import React, { useState, useRef, useEffect } from "react";
import {
  Type, Image, Wand2, Video, Film, LayoutGrid,
  ChevronDown, PanelLeftClose, PanelLeftOpen,
  FolderOpen, Upload, Link, Undo2, Redo2,
  Layers, Clapperboard, ScrollText, StickyNote, Frame, Bot, ListTree,
} from "lucide-react";
import { useDelayedUnmount } from "../../hooks/useDelayedUnmount";

// 画布左上角创建工具栏
// 所有动作均由 NodeCanvas 传入（复用现有 handler，不新写业务逻辑）
// actions: {
//   onAddText, onAddStickyNote, onAddGroupFrame, onOpenLibrary, onUpload, onOpenUrl,
//   onAddGenerateImage, onAddGenerateVideo,
//   onImportShots, onImportMiniStoryboard, onImportStoryboardBoard, onAutoLayout,
//   onUndo, onRedo, canUndo, canRedo, onToggleNodeList, nodeListOpen,
// }
export default function CanvasToolbar({ actions }) {
  const [collapsed, setCollapsed] = useState(false); // 小屏可折叠，默认展开
  const [imageMenuOpen, setImageMenuOpen] = useState(false); // 图片来源下拉
  const imageMenuRef = useRef(null);
  const imageMenuAnim = useDelayedUnmount(imageMenuOpen, 150);

  // 点击下拉外部时收起
  useEffect(() => {
    if (!imageMenuOpen) return;
    const handleClickOutside = (e) => {
      if (imageMenuRef.current && !imageMenuRef.current.contains(e.target)) {
        setImageMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [imageMenuOpen]);

  const btnClass =
    "h-8 px-2.5 flex items-center gap-1.5 rounded-md text-xs transition-colors duration-150 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]";
  const iconBtnClass =
    "w-7 h-8 flex items-center justify-center rounded-md transition-colors duration-150 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]";

  const imageSources = [
    { icon: <FolderOpen size={13} />, label: '资源库', action: actions.onOpenLibrary },
    { icon: <Upload size={13} />, label: '本地上传', action: actions.onUpload },
    { icon: <Link size={13} />, label: 'URL 链接', action: actions.onOpenUrl },
  ];

  return (
    <div
      className="absolute top-3 left-3 z-10 flex items-center gap-0.5 rounded-lg px-1 py-1"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border-secondary)',
        // P4 悬浮阴影，与节点卡片同一体系
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 24px -8px rgba(0,0,0,0.5), 0 32px 64px -24px rgba(0,0,0,0.35)',
      }}
    >
      {/* 折叠/展开 */}
      <button
        className={`${iconBtnClass} text-[var(--color-text-muted)]`}
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? '展开工具栏' : '收起工具栏'}
      >
        {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
      </button>

      {/* 节点导航始终可用；即使创建工具栏收起，也能打开左侧节点列表。 */}
      <button
        type="button"
        className={`${iconBtnClass} cursor-pointer`}
        style={{
          color: actions.nodeListOpen ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)',
          backgroundColor: actions.nodeListOpen ? 'var(--color-accent-light)' : 'transparent',
        }}
        onClick={actions.onToggleNodeList}
        title={actions.nodeListOpen ? '收起节点列表' : '展开节点列表'}
        aria-label={actions.nodeListOpen ? '收起节点列表' : '展开节点列表'}
        aria-pressed={!!actions.nodeListOpen}
      >
        <ListTree size={14} />
      </button>

      {!collapsed && (
        <div className="flex items-center gap-0.5 animate-in fade-in duration-150">
          <div className="w-px h-4 mx-0.5" style={{ backgroundColor: 'var(--color-border-secondary)' }} />

          {/* 撤销/重做 */}
          <button
            className={actions.canUndo ? iconBtnClass : `${iconBtnClass} opacity-35 cursor-default hover:bg-transparent hover:text-[var(--color-text-secondary)]`}
            onClick={actions.onUndo}
            disabled={!actions.canUndo}
            title="撤销 (Ctrl/⌘+Z)"
          >
            <Undo2 size={13} />
          </button>
          <button
            className={actions.canRedo ? iconBtnClass : `${iconBtnClass} opacity-35 cursor-default hover:bg-transparent hover:text-[var(--color-text-secondary)]`}
            onClick={actions.onRedo}
            disabled={!actions.canRedo}
            title="重做 (Ctrl/⌘+Shift+Z)"
          >
            <Redo2 size={13} />
          </button>

          <div className="w-px h-4 mx-0.5" style={{ backgroundColor: 'var(--color-border-secondary)' }} />

          {/* 文本 */}
          <button className={btnClass} onClick={actions.onAddText}>
            <Type size={13} />
            <span>文本</span>
          </button>

          {/* 便签（片4） */}
          <button className={btnClass} onClick={actions.onAddStickyNote} title="新建便签（双击编辑文字）">
            <StickyNote size={13} />
            <span>便签</span>
          </button>

          {/* 分组框（片4；有选中节点时自动归入） */}
          <button className={btnClass} onClick={actions.onAddGroupFrame} title="新建分组框（有选中节点时自动归入）">
            <Frame size={13} />
            <span>分组</span>
          </button>

          {/* AI 导演（片5；对话节点，连入节点作为上下文） */}
          <button className={btnClass} onClick={actions.onAddAiDirector} title="新建 AI 导演对话节点（连一些节点给它，就能看着它们聊）">
            <Bot size={13} />
            <span>AI 导演</span>
          </button>

          {/* 图片（来源下拉） */}
          <div className="relative" ref={imageMenuRef}>
            <button
              className={btnClass}
              onClick={() => setImageMenuOpen(!imageMenuOpen)}
            >
              <Image size={13} />
              <span>图片</span>
              <ChevronDown size={11} className={`transition-transform ${imageMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {imageMenuAnim.mounted && (
              <div
                className={`absolute top-full left-0 mt-1 rounded-lg overflow-hidden py-1 min-w-[120px] ${imageMenuAnim.exiting ? "animate-out fade-out slide-out-to-bottom-2 duration-150" : "animate-in fade-in slide-in-from-bottom-2 duration-200"}`}
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border-primary)',
                  boxShadow: 'var(--shadow-dropdown)',
                }}
              >
                {imageSources.map((item) => (
                  <button
                    key={item.label}
                    className="w-full px-3 py-1.5 flex items-center gap-2 text-left text-xs transition-colors duration-150 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]"
                    onClick={() => {
                      setImageMenuOpen(false);
                      item.action?.();
                    }}
                  >
                    <span style={{ color: 'var(--color-text-muted)' }}>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 生成图 */}
          <button className={btnClass} onClick={actions.onAddGenerateImage}>
            <Wand2 size={13} />
            <span>生成图</span>
          </button>

          {/* 生成视频 */}
          <button className={btnClass} onClick={actions.onAddGenerateVideo}>
            <Video size={13} />
            <span>生成视频</span>
          </button>

          {/* 剧本转分镜 */}
          <button className={btnClass} onClick={actions.onAddScriptToStoryboard} title="剧本 → 主镜 → 连接镜（先主镜后连接镜）">
            <ScrollText size={13} />
            <span>剧本转分镜</span>
          </button>

          <div className="w-px h-4 mx-0.5" style={{ backgroundColor: 'var(--color-border-secondary)' }} />

          {/* 导入分镜 */}
          <button className={btnClass} onClick={actions.onImportShots}>
            <Film size={13} />
            <span>导入分镜</span>
          </button>

          {/* 导入小故事板 */}
          <button className={btnClass} onClick={actions.onImportMiniStoryboard}>
            <Layers size={13} />
            <span>小故事板</span>
          </button>

          {/* 导入导演故事台 */}
          <button className={btnClass} onClick={actions.onImportStoryboardBoard}>
            <Clapperboard size={13} />
            <span>导演故事台</span>
          </button>

          {/* 整理画布 */}
          <button className={btnClass} onClick={actions.onAutoLayout}>
            <LayoutGrid size={13} />
            <span>整理</span>
          </button>
        </div>
      )}
    </div>
  );
}
